/*
 * Copyright (c) 2021 Santhosh Thottingal <santhosh.thottingal@gmail.com>
 * Released under the terms of the MIT license.
 */

import { getCECaretPosition, replaceTextInCE } from "./contenteditable.js";
import rules from "./rules.json";

/**
 * Bind swanalekha to the input field
 */
class Swanalekha {
	constructor(element, options = { enabled: false }) {
		this.rules = rules;
		this.patternStart = 0;
		this.element = element;
		this.pattern = "";
		this.tabCount = 0;
		this.enabled = options.enabled;

		if (this.enabled) {
			this.enable();
		}

		this.listen();
	}

	isToggleEvent(event) {
		// Ctrl+Shift+M
		return event.key === "M" && event.ctrlKey && event.shiftKey;
	}

	enable() {
		this.enabled = true;
		this.element.classList.add("swanalekha");
	}

	disable() {
		this.enabled = false;
		this.element.classList.remove("swanalekha");
	}

	toggle() {
		if (this.enabled) {
			this.disable();
		} else {
			this.enable();
		}
	}

	keyDownHandler(event) {
		if (this.isToggleEvent(event)) {
			this.toggle();
			event.preventDefault();
			event.stopPropagation();
			return false;
		}
		if (this.enabled) {
			this.process(event);
		}
	}

	listen() {
		this.element.addEventListener("keydown", this.keyDownHandler.bind(this));
	}

	isContentEditable() {
		return this.element.getAttribute("contenteditable");
	}

	/**
	 * Returns an array [start, end] of the beginning
	 * and the end of the current selection in element
	 */
	getCaretPosition(element) {
		if (this.isContentEditable()) {
			return getCECaretPosition(element);
		}
		return [element.selectionStart, element.selectionEnd];
	}

	replaceText(replacement, start, end) {
		if (this.isContentEditable()) {
			return replaceTextInCE(this.element, replacement, start, end);
		}

		const scrollTop = this.element.scrollTop;

		// Range selection is unreliable for complex scripts in some browsers.
		// Ref Bug https://bugs.webkit.org/show_bug.cgi?id=66630
		this.element.value =
			this.element.value.substring(0, start) +
			replacement +
			this.element.value.substring(end);

		this.element.scrollTop = scrollTop;
		this.element.selectionStart = this.element.selectionEnd =
			start + replacement.length;
	}

	process(event) {
		if (event.ctrlKey || event.altKey || event.metaKey) {
			return true;
		}
		// Use the same keys that keypress gave: characters, Tab and Enter.
		// Other keys (Shift, Backspace, arrows, dead keys, IME composition)
		// have a key name longer than one character.
		const char = { Tab: "\t", Enter: "\r" }[event.key] ?? event.key;
		if (char.length !== 1) {
			return true;
		}
		const transliteration = this.transliterate(char);
		if (transliteration) {
			const pos = this.getCaretPosition(this.element);
			const start = this.patternStart;
			const end = pos[1];
			this.replaceText(transliteration, start, end);
			// addEventListener ignores the return value, so stop the default
			// action here. For Tab, this also keeps the focus in the element.
			event.preventDefault();
			return false;
		}
		if (char === "\t") {
			return false;
		}
		if (this.tabCount > 1) {
			event.preventDefault();
		}
		return true;
	}

	transliterate(char) {
		if (char === "\t") {
			// Tab key
			this.tabCount++;
			if (this.pattern !== null && this.pattern !== "") {
				if (this.tabCount === 2) {
					this.pattern = this.pattern + this.tabCount;
				} else if (this.rules[this.pattern.slice(0, -1) + this.tabCount]) {
					this.pattern = this.pattern.slice(0, -1) + this.tabCount;
				} else {
					this.tabCount = 1;
					this.pattern = this.pattern.slice(0, -1);
				}
			}
		} else {
			this.pattern = this.pattern + char;
		}

		if (this.pattern.length > 5) {
			this.pattern = "";
			this.tabCount = 1;
		}

		let mal = this.rules[this.pattern];
		if (!mal) {
			// Diverge point
			this.pattern = char;
			this.tabCount = 1;
			this.patternStart = this.getCaretPosition(this.element)[0];
			mal = this.rules[this.pattern];
		}
		return mal;
	}
}

export default Swanalekha;
